import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Datastore } from '@freelancer/datastore';
import type { LoadshiftSiteStatsCollection } from '@freelancer/datastore/collections';
import { ContainerSize } from '@freelancer/ui/container';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { LinkColor, LinkHoverColor } from '@freelancer/ui/link';
import { BackgroundColor, LogoSize } from '@freelancer/ui/logo';
import type { SocialUrls } from '@freelancer/ui/social-buttons';
import { SocialButtonColor } from '@freelancer/ui/social-buttons';
import { CargoCategoryDetailPages } from 'app/freightlancer/freightlancer-cargo-category-detail-pages/freightlancer-cargo-category-detail-pages.model';
import type { CargoCategory } from 'app/freightlancer/freightlancer.model';
import type { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-footer-freightlancer',
  template: `
    <div
      class="FooterSection"
      flTrackingSection="Footer"
    >
      <fl-container
        [size]="size"
        [mobilePadding]="true"
      >
        <fl-grid class="mb-small">
          <fl-col
            class="mr-large"
            [colTablet]="12"
            [colDesktopSmall]="3"
          >
            <div class="FooterInfo mb-small tablet:mb-large">
              <fl-link
                flTrackingLabel="flLogo"
                [link]="'/'"
              >
                <fl-logo
                  [size]="LogoSize.MID"
                  [backgroundColor]="BackgroundColor.DARK"
                >
                </fl-logo>
              </fl-link>
            </div>
          </fl-col>
          @if (isReady$ | flAsync) {
            <fl-col
              class="FooterNav"
              [colTablet]="12"
              [colDesktopSmall]="5"
            >
              <fl-grid class="mb-xxxsmall">
                <fl-col
                  class="mb-small"
                  [colTablet]="6"
                >
                  <p
                    class="text-large text-light BackgroundDark font-bold FooterTitle"
                    i18n="Company counter number"
                  >
                    {{ totalFreightKmInLastYear$ | flAsync | number: '1.0-0' }}
                    km
                  </p>
                  <p
                    class="text-small text-light BackgroundDark"
                    i18n="Freight km posted counter label"
                  >
                    Moved in the last 12 months
                  </p>
                </fl-col>
                <fl-col [colTablet]="6">
                  <p
                    class="text-large text-light BackgroundDark font-bold FooterTitle"
                    i18n="Company counter number"
                  >
                    {{ totalLoadsLastYear$ | flAsync | number: '1.0-0' }}
                  </p>
                  <p
                    class="text-small text-light BackgroundDark"
                    i18n="Loads posted counter label"
                  >
                    Loads posted per year
                  </p>
                </fl-col>
              </fl-grid>
            </fl-col>
          }
        </fl-grid>
        <fl-grid class="tablet:mb-small desktop:mb-large">
          <fl-col
            class="desktop:mr-large"
            [colTablet]="3"
            [colDesktopSmall]="2"
            [colOrder]="1"
            [colTabletOrder]="3"
            [pull]="'right'"
          >
            <span
              class="text-light text-small FooterTitle font-bold mb-xxxsmall"
              i18n="Footer column title"
            >
              Contact Us
            </span>
            <div class="FooterNav mb-small">
              <div class="ContactInfoRow mb-xxxsmall">
                <fl-icon
                  class="mr-xxxsmall"
                  [color]="IconColor.LIGHT"
                  [name]="'ui-phone'"
                  [size]="IconSize.SMALL"
                ></fl-icon>
                <fl-link
                  flTrackingLabel="Footer phone link"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                  [link]="'tel:1300 562 374'"
                >
                  1300 562 374
                </fl-link>
              </div>
              <div class="ContactInfoRow mb-xxxsmall">
                <fl-icon
                  class="mr-xxxsmall"
                  [color]="IconColor.LIGHT"
                  [name]="'ui-mail'"
                  [size]="IconSize.SMALL"
                ></fl-icon>
                <fl-link
                  flTrackingLabel="FooterEmailLink"
                  i18n="Footer email label"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                  [link]="'mailto:info@loadshift.com.au'"
                >
                  info&#64;loadshift.com.au
                </fl-link>
              </div>
            </div>
          </fl-col>
          <fl-col
            class="mr-large"
            [colTablet]="3"
            [colDesktopSmall]="3"
            [colOrder]="2"
            [colTabletOrder]="1"
          >
            <span
              class="text-light text-small FooterTitle font-bold mb-xxxsmall"
              i18n="Footer column title"
            >
              Getting Around
            </span>
            <div class="FooterNav mb-small">
              <div class="mb-xxxsmall">
                <fl-link
                  link="/"
                  i18n="Footer link label"
                  flTrackingLabel="goToHome"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Home
                </fl-link>
              </div>
              <div class="mb-xxxsmall">
                <fl-link
                  link="/about"
                  i18n="Footer link label"
                  flTrackingLabel="goToAbout"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  About Us
                </fl-link>
              </div>
              <div class="mb-xxxsmall">
                <fl-link
                  link="/how-it-works/shippers"
                  i18n="Footer shippers link label"
                  flTrackingLabel="goToShippersPage"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Shippers
                </fl-link>
              </div>
              <div class="mb-xxxsmall">
                <fl-link
                  link="/how-it-works/carriers"
                  i18n="Footer shippers link label"
                  flTrackingLabel="goToShippersPage"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Carriers
                </fl-link>
              </div>
              <div class="mb-xxxsmall">
                <fl-link
                  link="/credit-terms/butn"
                  i18n="Footer butn link label"
                  flTrackingLabel="goToButnPage"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Credit Terms
                </fl-link>
              </div>
              <div class="mb-xxxsmall">
                <fl-link
                  link="/enterprise"
                  i18n="Enterprise link label"
                  flTrackingLabel="goToEnterprisePage"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Enterprise
                </fl-link>
              </div>
              <div class="mb-xxxsmall">
                <fl-link
                  link="/tenders"
                  i18n="Tenders link label"
                  flTrackingLabel="goToTendersPage"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Tenders
                </fl-link>
              </div>
              <div class="mb-xxxsmall">
                <fl-link
                  link="/faqs"
                  i18n="Footer link label"
                  flTrackingLabel="goToFAQ"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  FAQ
                </fl-link>
              </div>
              <div class="mb-xxxsmall">
                <fl-link
                  link="/transport-routes"
                  i18n="Footer transport routes link label"
                  flTrackingLabel="goToTransportRoutes"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Popular Freight Transport Routes
                </fl-link>
              </div>
              <div class="mb-xxxsmall">
                <fl-link
                  link="/search/loads"
                  i18n="Footer link label"
                  flTrackingLabel="goToFindLoad"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Find a Load
                </fl-link>
              </div>
              <div class="mb-xxxsmall">
                <fl-link
                  link="/login"
                  i18n="Footer link label"
                  flTrackingLabel="goToLogIn"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Log In
                </fl-link>
              </div>
              <div class="mb-xxxsmall">
                <fl-link
                  link="/signup"
                  i18n="Footer link label"
                  flTrackingLabel="goToSignUp"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                >
                  Sign Up
                </fl-link>
              </div>
            </div>
          </fl-col>
          <fl-col
            [colTablet]="5"
            [colDesktopSmall]="5"
            [colOrder]="3"
            [colTabletOrder]="2"
          >
            <span
              class="text-light text-small FooterTitle font-bold mb-xxxsmall"
              i18n="Footer categories column title"
            >
              Cargo Categories
            </span>
            <div class="FooterNav mb-small">
              <div class="CargoCategories">
                @for (
                  category of cargoCategories;
                  track trackByLink($index, category)
                ) {
                  <div class="CargoCategories-item mb-xsmall">
                    <fl-link
                      flTrackingLabel="CargoCategoryLink"
                      i18n="Category Name"
                      [color]="LinkColor.MID"
                      [hoverColor]="LinkHoverColor.LIGHT"
                      [link]="'/cargo-categories/' + category.link"
                    >
                      {{ category.name }}
                    </fl-link>
                  </div>
                }
              </div>
            </div>
          </fl-col>
          <fl-col
            class="desktop:mr-large"
            [colTablet]="3"
            [colDesktopSmall]="2"
            [colOrder]="3"
            [colTabletOrder]="3"
            [pull]="'right'"
          >
            <div class="PartnersAndTerms">
              <span
                class="text-light text-small FooterTitle font-bold mb-xxxsmall"
                i18n="Footer column title"
              >
                Partners
              </span>
              <div class="FooterNav mb-small">
                <fl-link
                  class="FooterNav-item"
                  link="https://www.freelancer.com"
                  i18n="Footer link label"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                  [flTrackingLabel]="'goToFreelancer'"
                >
                  Freelancer.com
                </fl-link>
                <fl-link
                  class="FooterNav-item"
                  link="https://www.escrow.com"
                  i18n="Footer link label"
                  [color]="LinkColor.MID"
                  [hoverColor]="LinkHoverColor.LIGHT"
                  [flTrackingLabel]="'goToEscrow'"
                >
                  Escrow.com
                </fl-link>
              </div>
              <span
                class="text-light text-small FooterTitle font-bold mb-xxxsmall"
                i18n="Footer column title"
              >
                Terms
              </span>
              <div class="FooterNav mb-small">
                <div class="mb-xxxsmall">
                  <fl-link
                    link="/about/terms"
                    i18n="Footer link label"
                    flTrackingLabel="goToTerms"
                    [color]="LinkColor.MID"
                    [hoverColor]="LinkHoverColor.LIGHT"
                  >
                    Terms of Use
                  </fl-link>
                </div>
                <div class="mb-xxxsmall">
                  <fl-link
                    flTrackingLabel="goToCodeOfConduct"
                    i18n="Code of Conduct link"
                    link="/info/codeofconduct"
                    [color]="LinkColor.MID"
                    [hoverColor]="LinkHoverColor.LIGHT"
                  >
                    Code of Conduct
                  </fl-link>
                </div>
                <div class="mb-xxxsmall">
                  <fl-link
                    link="/about/privacy"
                    i18n="Footer link label"
                    flTrackingLabel="goToPrivacyPolicy"
                    [color]="LinkColor.MID"
                    [hoverColor]="LinkHoverColor.LIGHT"
                  >
                    Privacy Policy
                  </fl-link>
                </div>
              </div>
            </div>
          </fl-col>
        </fl-grid>

        <fl-grid [vAlign]="VerticalAlignment.VERTICAL_CENTER">
          <fl-col
            [colTablet]="6"
            [colOrder]="2"
            [colTabletOrder]="1"
          >
            <p class="text-light">
              {{ 'Copyright &copy;' }} {{ currentTime | date: 'yyyy' }}
              {{ 'Loadshift Pty Ltd' }}
            </p>
          </fl-col>
          <fl-col
            class="mb-xsmall"
            [colTablet]="2"
            [colDesktopSmall]="3"
            [colOrder]="1"
            [colTabletOrder]="2"
            [pull]="'right'"
          >
            <div class="FreightlancerSocialIcons">
              <fl-social-buttons
                [color]="SocialButtonColor.LIGHT"
                [customSocialUrls]="socialUrls"
                [size]="IconSize.LARGE"
              ></fl-social-buttons>
            </div>
          </fl-col>
        </fl-grid>
      </fl-container>
    </div>
  `,
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterFreightlancerComponent implements OnInit {
  BackgroundColor = BackgroundColor;
  IconColor = IconColor;
  IconSize = IconSize;
  LinkColor = LinkColor;
  LinkHoverColor = LinkHoverColor;
  LogoSize = LogoSize;
  SocialButtonColor = SocialButtonColor;
  VerticalAlignment = VerticalAlignment;

  isReady$: Observable<boolean>;
  totalFreightKmInLastYear$: Observable<number>;
  totalLoadsLastYear$: Observable<number>;

  cargoCategories: readonly CargoCategory[] = [
    {
      name: 'Boat Transport',
      link: CargoCategoryDetailPages.BOAT_TRANSPORT,
    },
    {
      name: 'Bulk Tipper Transport',
      link: CargoCategoryDetailPages.BULK_TIPPER_TRANSPORT,
    },
    {
      name: 'Car Transport',
      link: CargoCategoryDetailPages.CAR_TRANSPORT,
    },
    {
      name: 'Container Transport',
      link: CargoCategoryDetailPages.CONTAINER_TRANSPORT,
    },
    {
      name: 'Furniture Removal',
      link: CargoCategoryDetailPages.FURNITURE_REMOVAL,
    },
    {
      name: 'General Freight',
      link: CargoCategoryDetailPages.GENERAL_FREIGHT,
    },
    {
      name: 'Grain Transport',
      link: CargoCategoryDetailPages.GRAIN_TRANSPORT,
    },
    {
      name: 'Hay Transport',
      link: CargoCategoryDetailPages.HAY_TRANSPORT,
    },
    {
      name: 'Heavy Haulage',
      link: CargoCategoryDetailPages.HEAVY_HAULAGE,
    },
    {
      name: 'Liquid Tanker Transport',
      link: CargoCategoryDetailPages.LIQUID_TANKER,
    },
    {
      name: 'Livestock Transport',
      link: CargoCategoryDetailPages.LIVESTOCK_TRANSPORT,
    },
    {
      name: 'Machinery Transport',
      link: CargoCategoryDetailPages.MACHINERY_TRANSPORT,
    },
    {
      name: 'Palletised Freight',
      link: CargoCategoryDetailPages.PALLETISED_FREIGHT,
    },
    {
      name: 'Portable Buildings',
      link: CargoCategoryDetailPages.PORTABLE_BUILDINGS,
    },
    {
      name: 'Trailer Transport',
      link: CargoCategoryDetailPages.TRAILER_TRANSPORT,
    },
    {
      name: 'Truck Transport',
      link: CargoCategoryDetailPages.TRUCK_TRANSPORT,
    },
  ];

  readonly currentTime: number = Date.now();
  size: ContainerSize;
  socialUrls: SocialUrls = {
    facebook: 'https://www.facebook.com/loadshift',
    twitter: 'https://x.com/loadshift',
    instagram: 'https://www.instagram.com/loadshift',
    linkedin: 'https://www.linkedin.com/company/freightlancer/',
  };

  @Input() set containerSize(value: ContainerSize) {
    this.size = value || ContainerSize.DESKTOP_LARGE;
  }

  constructor(private datastore: Datastore) {}

  trackByLink(_: number, item: CargoCategory): CargoCategoryDetailPages {
    return item.link;
  }

  ngOnInit(): void {
    const siteStatsCollection =
      this.datastore.document<LoadshiftSiteStatsCollection>(
        'loadshiftSiteStats',
      );

    this.isReady$ = siteStatsCollection.status$.pipe(
      switchMap(status =>
        status.error
          ? [false]
          : siteStatsCollection.valueChanges().pipe(map(() => true)),
      ),
    );

    this.totalFreightKmInLastYear$ = siteStatsCollection
      .valueChanges()
      .pipe(map(({ totalFreightKmInLastYear }) => totalFreightKmInLastYear));

    this.totalLoadsLastYear$ = siteStatsCollection
      .valueChanges()
      .pipe(map(({ totalLoadsLastYear }) => totalLoadsLastYear));
  }
}
